"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import {
  type Product as OctoProduct,
  getProductBrandLabel,
  getProductImage,
  getProductName,
  productHasColorVariants,
} from "@/lib/product-utils";
import {
  type Cart,
  type VariantSpec,
  addItemsToCart,
  createCart,
  getUserCarts,
  removeItemsFromCart,
  updateCart,
  hydrateProductByUuid,
  hydrateProducts,
} from "@/lib/shop-agent-client";
// Note: Availability checks are disabled for MVP; keep logic but comment imports
// import { checkProductAvailability } from '@/lib/shop-agent-client';

export interface CartUIItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
  url?: string;
  size?: string;
  color?: string;
  hasColorVariants?: boolean;
  quantity: number;
  retailer: string;
  retailerLogo?: string;
  uuid?: string;
  availableSizes?: string[];
  isActive?: boolean;
  catalog?: string;
}

interface CartContextType {
  carts: Cart[];
  defaultCart: Cart | null;
  loading: boolean;
  hydrating: boolean;
  // UI derived items (hydrated from catalog)
  items: CartUIItem[];
  getItemsByRetailer: () => Record<string, CartUIItem[]>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getRetailerSubtotal: (retailer: string) => number;
  isInCart: (productId: string) => boolean;
  addItem: (productId: string, variantSpec?: VariantSpec) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  refreshCarts: () => Promise<void>;
  clearCart: (cartId?: string) => Promise<void>;
  createTemporaryCart: (productId: string, variantSpec?: VariantSpec) => Promise<Cart>;
  getOrCreateDefaultCart: () => Promise<Cart>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const userId = user?.uid ?? null;

  const [carts, setCarts] = useState<Cart[]>([]);
  const [defaultCart, setDefaultCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<CartUIItem[]>([]);
  const [hydrating, setHydrating] = useState<boolean>(false);

  /**
   * Fetch all carts for the current user.
   */
  const fetchCarts = useCallback(
    async (options?: { showLoading?: boolean; preserveStateOnError?: boolean }) => {
      const showLoading = options?.showLoading ?? true;
      const preserveStateOnError = options?.preserveStateOnError ?? false;

      // If auth not resolved yet, avoid clearing state; keep loading
      if (!userId) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }
      try {
        const userCarts = await getUserCarts();
        setCarts(userCarts);

        // Always use the 0th index cart from the API for UI
        let nextDefault = userCarts[0];

        if (!nextDefault) {
          try {
            const created = await createCart("default");
            nextDefault = created;
            // refetch to get consistent state
            const refreshed = await getUserCarts();
            setCarts(refreshed);
          } catch (e) {
            console.warn("Failed to auto-create default cart", e);
          }
        }

        setDefaultCart(nextDefault || null);
      } catch (err) {
        console.warn("Failed to fetch carts", err);
        if (!preserveStateOnError) {
          setCarts([]);
          setDefaultCart(null);
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [userId]
  );
  const skipNextHydrationRef = useRef(false);
  const hydrationRunIdRef = useRef(0);

  useEffect(() => {
    // Defer fetch until after initial mount to avoid SSR/client auth race
    const id = setTimeout(() => {
      fetchCarts();
    }, 0);
    return () => clearTimeout(id);
  }, [fetchCarts]);

  // Hydrate UI items when defaultCart is ready
  useEffect(() => {
    let isActive = true;
    const runId = (hydrationRunIdRef.current += 1);

    const buildItems = async () => {
      if (skipNextHydrationRef.current) {
        skipNextHydrationRef.current = false;
        if (isActive) {
          setHydrating(false);
        }
        return;
      }

      if (!defaultCart) {
        if (isActive) {
          setItems([]);
          setHydrating(false);
        }
        return;
      }
      if (!defaultCart.product_ids || defaultCart.product_ids.length === 0) {
        if (isActive) {
          setItems([]);
          setHydrating(false);
        }
        return;
      }
      if (isActive) {
        setHydrating(true);
      }
      // One batched request for the whole cart; results follow product_ids.
      let hydrated: Awaited<ReturnType<typeof hydrateProducts>>;
      try {
        hydrated = await hydrateProducts(defaultCart.product_ids);
      } catch (e) {
        // An outage keeps the items already shown; it does not clear the cart.
        console.warn("Failed to hydrate cart products", e);
        if (isActive && hydrationRunIdRef.current === runId) {
          setHydrating(false);
        }
        return;
      }
      const next: CartUIItem[] = [];
      for (let i = 0; i < defaultCart.product_ids.length; i++) {
        const productId = defaultCart.product_ids[i];
        const variant = defaultCart.product_variants?.[i] as VariantSpec | undefined;
        try {
          // Catalog product has a broad shape; keep flexible but typed as unknown
          type CatalogProduct = OctoProduct & {
            brand?: { name?: string } | string;
            brand_name?: string;
            catalog?: string;
            url?: string;
          };
          const hydratedProduct = hydrated[i]?.product;
          if (!hydratedProduct) {
            throw new Error("Product not available");
          }
          const product = hydratedProduct as unknown as CatalogProduct;
          // retailer/brand
          const brandLabel = getProductBrandLabel(product as unknown as OctoProduct);
          const brand = brandLabel || "Brand";
          const retailer = (brandLabel || "Retailer").toString();
          // price (use normalized field only, no offers fallback)
          const price = typeof product?.current_price === "number" ? product.current_price : 0;
          // image via product-utils (handles gs:// and fallbacks)
          const utilsImage = getProductImage(product as unknown as OctoProduct);
          const image =
            utilsImage && utilsImage !== "/placeholder.jpg"
              ? utilsImage
              : "/api/placeholder/120/160";

          // Get product sizes (if available)
          const productSizes = (product as unknown as { sizes?: string[] })?.sizes;
          const availableSizes =
            Array.isArray(productSizes) && productSizes.length > 0 ? productSizes : undefined;

          const isProductActive =
            (product as unknown as { is_active?: boolean | null }).is_active ?? true;

          next.push({
            id: productId,
            uuid: productId,
            name: getProductName(product as unknown as OctoProduct),
            brand: brand || "Brand",
            price,
            image: image || "/api/placeholder/120/160",
            url: (product as unknown as { url?: string }).url,
            size: (variant as unknown as { size?: string })?.size || undefined,
            color: (variant as unknown as { color?: string })?.color || undefined,
            hasColorVariants: productHasColorVariants(product as unknown as OctoProduct),
            quantity: 1,
            retailer: retailer,
            availableSizes,
            isActive: isProductActive,
            catalog: product?.catalog || undefined,
          });
        } catch (e) {
          // skip bad item but keep cart integrity
          console.warn("Failed to hydrate product", productId, e);
        }
      }
      if (!isActive || hydrationRunIdRef.current !== runId) {
        return;
      }
      setItems(next);
      setHydrating(false);
    };

    buildItems();

    return () => {
      isActive = false;
    };
  }, [defaultCart]);

  /**
   * Get or create the default cart for the user
   */
  const getOrCreateDefaultCart = useCallback(async (): Promise<Cart> => {
    if (defaultCart) {
      return defaultCart;
    }

    if (!userId) {
      throw new Error("User not authenticated");
    }

    // If we already have carts loaded, use the first one
    if (carts.length > 0) {
      const first = carts[0];
      setDefaultCart(first);
      return first;
    }

    try {
      // Fetch latest carts to avoid creating duplicates during race conditions
      const latest = await getUserCarts();
      if (latest && latest.length > 0) {
        setCarts(latest);
        setDefaultCart(latest[0]);
        return latest[0];
      }

      // Create a new default cart only if user truly has none
      const newCart = await createCart("default");
      await fetchCarts(); // Refresh all carts
      return newCart;
    } catch (err) {
      console.error("Failed to get or create default cart", err);
      throw err;
    }
  }, [userId, defaultCart, carts, fetchCarts]);

  const isInCart = useCallback(
    (productId: string) => {
      return !!defaultCart?.product_ids.includes(productId);
    },
    [defaultCart]
  );

  // UI helpers
  const getItemsByRetailer = useCallback(() => {
    return items.reduce(
      (acc, item) => {
        if (!acc[item.retailer]) acc[item.retailer] = [];
        acc[item.retailer].push(item);
        return acc;
      },
      {} as Record<string, CartUIItem[]>
    );
  }, [items]);

  const getTotalItems = useCallback(() => {
    return items.reduce((t, it) => t + it.quantity, 0);
  }, [items]);

  const getTotalPrice = useCallback(() => {
    return items.reduce((t, it) => t + it.price * it.quantity, 0);
  }, [items]);

  const getRetailerSubtotal = useCallback(
    (retailer: string) => {
      return items
        .filter((i) => i.retailer === retailer)
        .reduce((t, it) => t + it.price * it.quantity, 0);
    },
    [items]
  );

  const addItem = useCallback(
    async (productId: string, variantSpec?: VariantSpec) => {
      const cart = await getOrCreateDefaultCart();
      if (cart.product_ids.includes(productId)) return;

      setLoading(true);
      try {
        // Availability check disabled in MVP; keep hydration in place for future use
        try {
          await hydrateProductByUuid(productId);
        } catch (err) {
          console.warn("Could not fetch product details for availability check", err);
        }

        // Check availability if we have a product URL and variant specification
        // if (productUrl && variantSpec) {
        //   try {
        //     const availabilityResponse = await checkProductAvailability(productUrl, variantSpec);

        //     if (!availabilityResponse.availability.in_stock) {
        //       // Product variant is not in stock - show alert and don't add to cart
        //       const explanation = availabilityResponse.availability.explanation ||
        //                          'This product variant is currently out of stock.';
        //       alert(`Unable to add to cart: ${explanation}`);
        //       return;
        //     }
        //   } catch (err) {
        //     // If availability check fails, log the error but continue with adding to cart
        //     // This ensures the cart functionality still works even if availability check fails
        //     console.warn('Availability check failed, proceeding with add to cart:', err);
        //   }
        // }

        // Add item to cart
        await addItemsToCart(cart.id, [productId], variantSpec ? [variantSpec] : undefined);
        await fetchCarts(); // Refresh all carts
      } catch (err) {
        console.error("Failed to add item to cart", err);
        alert("Failed to add item to cart. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [getOrCreateDefaultCart, fetchCarts]
  );

  const removeItem = useCallback(
    async (productId: string) => {
      if (!defaultCart || !defaultCart.product_ids.includes(productId)) return;

      const computeNextCartState = (cart: Cart) => {
        const nextProductIds = cart.product_ids.filter((id) => id !== productId);
        const nextVariants = cart.product_variants
          ? cart.product_variants.filter((_, index) => cart.product_ids[index] !== productId)
          : undefined;
        return { nextProductIds, nextVariants };
      };

      hydrationRunIdRef.current += 1;
      skipNextHydrationRef.current = true;
      setDefaultCart((prev) => {
        if (!prev || !prev.product_ids.includes(productId)) return prev;
        const { nextProductIds, nextVariants } = computeNextCartState(prev);
        return {
          ...prev,
          product_ids: nextProductIds,
          product_variants: nextVariants,
        };
      });
      setItems((prev) => prev.filter((item) => item.id !== productId));
      setCarts((prev) =>
        prev.map((cart) =>
          cart.id === defaultCart.id
            ? {
                ...cart,
                product_ids: computeNextCartState(cart).nextProductIds,
                product_variants: computeNextCartState(cart).nextVariants,
              }
            : cart
        )
      );

      try {
        await removeItemsFromCart(defaultCart.id, [productId]);
        await fetchCarts({ showLoading: false, preserveStateOnError: true });
      } catch (err) {
        console.error("Failed to remove item from cart", err);
        try {
          await fetchCarts({ showLoading: false, preserveStateOnError: true });
        } catch (fetchErr) {
          console.warn("Failed to refresh cart after removal failure", fetchErr);
        }
      }
    },
    [defaultCart, fetchCarts]
  );

  const clearCart = useCallback(
    async (cartId?: string) => {
      setLoading(true);
      try {
        const targetCartId = cartId || defaultCart?.id;
        if (!targetCartId) {
          console.warn("No cart to clear");
          return;
        }

        // Update cart to have empty products
        await updateCart(targetCartId, [], []);
        await fetchCarts(); // Refresh all carts
      } catch (err) {
        console.error("Failed to clear cart", err);
      } finally {
        setLoading(false);
      }
    },
    [defaultCart, fetchCarts]
  );

  /**
   * Create a temporary cart with a single product (for Buy Now functionality)
   */
  const createTemporaryCart = useCallback(
    async (productId: string, variantSpec?: VariantSpec): Promise<Cart> => {
      try {
        if (!userId) {
          throw new Error("User not authenticated");
        }
        const tempCart = await createCart(
          "temp-" + Date.now(),
          [productId],
          variantSpec ? [variantSpec] : undefined
        );
        await fetchCarts(); // Refresh all carts
        return tempCart;
      } catch (err) {
        console.error("Failed to create temporary cart", err);
        throw err;
      }
    },
    [userId, fetchCarts]
  );

  return (
    <CartContext.Provider
      value={{
        carts,
        defaultCart,
        loading,
        hydrating,
        items,
        getItemsByRetailer,
        getTotalItems,
        getTotalPrice,
        getRetailerSubtotal,
        isInCart,
        addItem,
        removeItem,
        refreshCarts: fetchCarts,
        clearCart,
        createTemporaryCart,
        getOrCreateDefaultCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
